import Category from '../models/Category'
import Post from '../models/Post'
import CategoryTreeNode, {
  CategoryTreeNodeType,
} from '../models/CategoryTreeNode'
import ProductCustomization, {
  HeroTextAlignment,
  HeroSpacing,
  Instructor,
} from '@/models/ProductCustomization'
import LoginService from '@/services/LoginService'
import { fetchCat } from './permission.helper'
import router from '../router'
import {
  MAXIMUM_CHARACTER_LENGTH_BEFORE_READMORE,
  SLICED_TITLE_LENGTH,
} from './constants'
import store from '@/store'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
const nonAcceptableFileTypes = new Set([
  'application/x-msdownload',
  // 'application/zip',
  'dmg',
  'exe',
  // 'zip',
  'bat',
  'apk',
  'csh',
  'ipa',
  'msi',
  'reg',
  'jar',
  'cmd',
  'bin',
  'com',
])

import { useNotification } from '@gohighlevel/ghl-ui'

import { useRoute } from 'vue-router'
import restAgent from './restAgent'
const notification = useNotification()

export function createCategoryPostTree(
  parentNodeId: string | null,
  categories: Array<Category>
): Array<CategoryTreeNode> {
  const childs = categories
    .filter((category: Category) => category.parentCategory === parentNodeId)
    .sort((a, b) => a.sequenceNo - b.sequenceNo)

  const nodes = childs.map((c: Category) => {
    const node = new CategoryTreeNode(
      c,
      CategoryTreeNodeType.Category,
      parentNodeId || CategoryTreeNode.ROOT_PARENT_NODE
    )
    const subCategoryNodes = createCategoryPostTree(node.id, categories)

    const postsInCategory = c.posts || []
    const postNodes = postsInCategory.map(
      (p: Post) => new CategoryTreeNode(p, CategoryTreeNodeType.Post, node.id)
    )

    node.childs = [...subCategoryNodes, ...postNodes].sort(
      (a, b) => a.sequenceNo - b.sequenceNo
    )

    return node
  })

  return nodes
}

export function findLastCompletedPost(
  tree: Array<CategoryTreeNode>,
  completedPosts: Set<string>
): CategoryTreeNode | undefined {
  for (let i = tree.length - 1; i >= 0; i--) {
    const node = tree[i]

    if (
      node.type === CategoryTreeNodeType.Post &&
      completedPosts.has(node.id)
    ) {
      return node
    } else {
      const lastCompletedPostInSubtree = findLastCompletedPost(
        node.childs,
        completedPosts
      )
      if (lastCompletedPostInSubtree) {
        return lastCompletedPostInSubtree
      }
    }
  }
}

export function findFirstUncompletedPost(
  tree: Array<CategoryTreeNode>,
  completedPosts: Set<string>
): CategoryTreeNode | undefined {
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]

    if (
      node.type === CategoryTreeNodeType.Post &&
      !completedPosts.has(node.id)
    ) {
      return node
    } else {
      const firstUncompletedPostInSubtree = findFirstUncompletedPost(
        node.childs,
        completedPosts
      )

      if (firstUncompletedPostInSubtree) {
        return firstUncompletedPostInSubtree
      }
    }
  }
}

export function unwrapCategoryTree(tree: any, isSubCategory: boolean) {
  if (!tree?.node) return []
  const categoryNodes = []
  let nodes = {
    post: [],
    categoryTitle: tree.node.title,
    isSubCategory: isSubCategory,
    available: tree.node.available,
  }
  for (const node of tree.childs) {
    if (node.type === CategoryTreeNodeType.Post) {
      nodes['post'].push(node)
    } else if (node.type === CategoryTreeNodeType.Category) {
      if (nodes.post.length) {
        categoryNodes.push(nodes)
        nodes = {
          post: [],
          categoryTitle: tree.node.title,
          isSubCategory: isSubCategory,
          available: tree.node.available,
        }
      }
      categoryNodes.push(...unwrapCategoryTree(node, true))
    }
  }
  categoryNodes.push(nodes)

  return categoryNodes
}

function extractPostIdsFromCategoryTree(
  tree: Array<CategoryTreeNode>
): Array<CategoryTreeNode> {
  let postNodes = []

  for (const node of tree) {
    if (node.type === CategoryTreeNodeType.Category) {
      const nodesInCat = extractPostIdsFromCategoryTree(node.childs)
      postNodes = [...postNodes, ...nodesInCat]
    } else if (node.type === CategoryTreeNodeType.Post) {
      postNodes = [...postNodes, node]
    }
  }

  return postNodes
}

export function findNextPostToComplete(
  tree: Array<CategoryTreeNode>,
  lastCompletedPostId: string,
  completedPostIds: Set<string>
): CategoryTreeNode {
  const posts = extractPostIdsFromCategoryTree(tree)
  const lastCompletedPostIndex = posts.findIndex(
    (node: CategoryTreeNode) => node.id === lastCompletedPostId
  )

  if (lastCompletedPostIndex === -1) {
    return findFirstUncompletedPost(tree, completedPostIds)
  }

  // First find an uncompleted post after the last completed post
  for (let i = lastCompletedPostIndex + 1; i < posts.length; i++) {
    const post = posts[i]
    if (!completedPostIds.has(post.id)) {
      return post
    }
  }

  // If not found above, then find the first uncompleted post before completed one
  for (let i = 0; i < lastCompletedPostIndex; i++) {
    const post = posts[i]
    if (!completedPostIds.has(post.id)) {
      return post
    }
  }
}

// DEPRECATED: Use findNextPostToComplete now
export function findNextPost(
  tree: Array<CategoryTreeNode>,
  postId: string
): CategoryTreeNode | undefined {
  let postMatched = false
  let queue = [...tree]

  while (queue.length > 0) {
    const node = queue.shift()

    if (!node) return

    if (node.type === CategoryTreeNodeType.Post) {
      if (node.id === postId) {
        postMatched = true
      } else if (postMatched) {
        return node
      }
    } else {
      queue = [...queue, ...node.childs]
    }
  }
}

function getNode(categoryNode: any) {
  if (!categoryNode) {
    return null
  }
  const node = categoryNode
  if (node.type === CategoryTreeNodeType.Category) {
    return {
      categoryId: node.id,
      postId: node.childs[node.childs.length - 1]?.id,
    }
  }
  return { categoryId: node.node.categoryId, postId: node.id }
}

function getNextNode(categoryNode: any) {
  if (!categoryNode) {
    return null
  }
  const node = categoryNode
  if (node.type === CategoryTreeNodeType.Category) {
    return {
      categoryId: node.id,
      postId: node.childs[0]?.id,
    }
  }
  return { categoryId: node.node.categoryId, postId: node.id }
}

export function findPreviousPostId(
  categoryTree: Array<CategoryTreeNode>,
  postId: string,
  category: Category,
  playlist: []
) {
  const postIndex = playlist.findIndex(
    (node: CategoryTreeNode) => node.id === postId
  )
  if (postIndex > 0) {
    const data: CategoryTreeNode = playlist[postIndex - 1]
    if (data.type === CategoryTreeNodeType.Post) {
      return getNode(data)
    } else if (data.type === CategoryTreeNodeType.Category) {
      const categoryPost = categoryTree.find(
        (node: CategoryTreeNode) => node.id === data.node.parentCategory
      )
      const category = categoryPost?.childs[postIndex - 1]
      return getNode(category?.childs[category.childs.length - 1])
    }
  }
  let parentCategory
  if (category.parentCategory) {
    parentCategory = categoryTree.find(
      (node: CategoryTreeNode) => node.id === category.parentCategory
    )
    const categoryIndex = parentCategory?.childs.findIndex(
      (node: CategoryTreeNode) => node.id === category.id
    )
    if (categoryIndex > 0) {
      return getNode(parentCategory.childs[categoryIndex - 1])
    }
    const parentCategoryIndex = categoryTree.findIndex(
      (node: CategoryTreeNode) => node.id === category.parentCategory
    )
    if (parentCategoryIndex > 0) {
      const prevCategory = categoryTree[parentCategoryIndex - 1].childs.filter(
        (node: any) =>
          node.type === CategoryTreeNodeType.Post ||
          (node.type === CategoryTreeNodeType.Category && node.node.available)
      )
      return getNode(prevCategory[prevCategory.length - 1])
    } else {
      return null
    }
  } else {
    const postCategoryIndex = categoryTree.findIndex(
      (node: CategoryTreeNode) => node.id === category.id
    )
    if (postCategoryIndex > 0) {
      const prevCat = categoryTree[postCategoryIndex - 1].childs.filter(
        (node: any) =>
          node.type === CategoryTreeNodeType.Post ||
          (node.type === CategoryTreeNodeType.Category && node.node.available)
      )
      return getNode(prevCat[prevCat.length - 1])
    }
  }
  return null
}

export function findNextPostId(
  categoryTree: Array<CategoryTreeNode>,
  postId: string,
  category: Category,
  playlist: []
) {
  const postIndex = playlist.findIndex(
    (node: CategoryTreeNode) => node.id === postId
  )
  if (postIndex < playlist.length - 1) {
    const data: CategoryTreeNode = playlist[postIndex + 1]
    if (data.type === CategoryTreeNodeType.Post) {
      return getNextNode(data)
    } else if (data.type === CategoryTreeNodeType.Category) {
      const categoryPost = categoryTree.find(
        (node: CategoryTreeNode) => node.id === data.node.parentCategory
      )
      const category = categoryPost?.childs[postIndex + 1]
      return getNextNode(category?.childs[0])
    }
  }
  let parentCategory
  if (category.parentCategory) {
    parentCategory = categoryTree.find(
      (node: CategoryTreeNode) => node.id === category.parentCategory
    )
    const categoryIndex = parentCategory?.childs.findIndex(
      (node: CategoryTreeNode) => node.id === category.id
    )
    if (categoryIndex < parentCategory?.childs.length - 1) {
      return getNextNode(parentCategory.childs[categoryIndex + 1])
    }
    const parentCategoryIndex = categoryTree.findIndex(
      (node: CategoryTreeNode) => node.id === category.parentCategory
    )
    if (parentCategoryIndex < categoryTree.length - 1) {
      const nextCategory = categoryTree[parentCategoryIndex + 1].childs.filter(
        (node: any) =>
          node.type === CategoryTreeNodeType.Post ||
          (node.type === CategoryTreeNodeType.Category && node.node.available)
      )
      return getNextNode(nextCategory[0])
    } else {
      return null
    }
  } else {
    const postCategoryIndex = categoryTree.findIndex(
      (node: CategoryTreeNode) => node.id === category.id
    )
    if (postCategoryIndex < categoryTree.length - 1) {
      const nextCat = categoryTree[postCategoryIndex + 1].childs.filter(
        (node: any) =>
          node.type === CategoryTreeNodeType.Post ||
          (node.type === CategoryTreeNodeType.Category && node.node.available)
      )
      return getNextNode(nextCat[0])
    }
  }
  return null
}

export function getCurrentPostIndex(
  postId: string,
  category: Category,
  categoryTree: []
) {
  let index = 0,
    parentCategory: CategoryTreeNode
  if (category?.parentCategory) {
    category?.posts.sort((p1, p2) => p1.sequenceNo - p2.sequenceNo)
    index = category?.posts.findIndex((p) => p.id === postId)
    parentCategory = categoryTree.find(
      (node: CategoryTreeNode) => node.id === category.parentCategory
    )
    const categoryIndex = parentCategory?.childs?.findIndex(
      (node: CategoryTreeNode) => node.id === category.id
    )
    for (let i = 0; i < categoryIndex; i++) {
      const node = parentCategory?.childs[i]
      if (node.type === CategoryTreeNodeType.Category) {
        index += node.childs.length
      } else {
        index++
      }
    }
  } else {
    parentCategory = categoryTree.find(
      (node: CategoryTreeNode) => node.id === category.id
    )
    const postIndex = parentCategory?.childs?.findIndex(
      (node: CategoryTreeNode) => node.id === postId
    )
    for (let i = 0; i < postIndex; i++) {
      const node = parentCategory?.childs[i]
      if (node.type === CategoryTreeNodeType.Category) {
        index += node.childs.length
      } else {
        index++
      }
    }
  }
  return index + 1
}

function removeUnavailableCategories(categoryTree: Array<CategoryTreeNode>) {
  const availableNodes = categoryTree.filter((node: CategoryTreeNode) => {
    if (node.type === CategoryTreeNodeType.Category) {
      const cat = node.node as Category
      return cat.available
    }

    return true
  })

  return availableNodes.map((node: CategoryTreeNode) => {
    if (node.type === CategoryTreeNodeType.Category && node.childs) {
      node.childs = removeUnavailableCategories(node.childs)
    }
    return node
  })
}

export function computeNextPost(
  categoryTree: Array<CategoryTreeNode>,
  postIdsCompleted: Set<string>
): CategoryTreeNode {
  let nextPostToGo = null

  const tree = removeUnavailableCategories([...categoryTree])

  if (postIdsCompleted.size > 0) {
    const lastCompletedPost = findLastCompletedPost(tree, postIdsCompleted)

    // find the next post after the last post that is completed
    if (lastCompletedPost) {
      console.log('available tree --> ', tree)
      nextPostToGo = findNextPostToComplete(
        tree,
        lastCompletedPost.id,
        postIdsCompleted
      )
    }
  }

  if (!nextPostToGo) {
    // if there are no completed posts, or the last post in course is completed
    const firstUncompletedPost = findFirstUncompletedPost(
      tree,
      postIdsCompleted
    )
    if (firstUncompletedPost) {
      nextPostToGo = firstUncompletedPost
    }
    // else, couldn't find an uncompleted post
  }

  return nextPostToGo || null
}

function findPost(tree: Array<CategoryTreeNode>) {
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.type === CategoryTreeNodeType.Post) {
      return node
    } else {
      const post = findPost(node.childs)
      if (post) {
        return post
      }
    }
  }
  return null
}

export function findCategory(tree: Array<CategoryTreeNode>, id: string) {
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.type === CategoryTreeNodeType.Category && node.id === id) {
      return node
    } else {
      const category = findCategory(node.childs, id)
      if (category) {
        return category
      }
    }
  }
  return null
}

export function getPost(categoryTree: Array<CategoryTreeNode>) {
  const tree = removeUnavailableCategories([...categoryTree])
  const post = findPost(tree)
  return post
}

export function getHeroAlignmentClass(textAlignment: string) {
  let alignment = ''

  switch (textAlignment) {
    case HeroTextAlignment.left:
      alignment = 'text-left'
      break
    case HeroTextAlignment.centered:
      alignment = 'text-center'
      break
    case HeroTextAlignment.right:
      alignment = 'text-right'
      break
    default:
      alignment = 'text-center'
  }

  return alignment
}

export function getSections(product: any) {
  if (product && product.customizations && product.customizations.settings) {
    return product.customizations.settings.sections
  }
  return null
}
export function getThemeSettings(product: any) {
  if (product && product.customizations && product.customizations.settings) {
    return product.customizations.settings.themeSettings
  }
  return null
}
export function getProgressBarColor(product: any) {
  let color = '#3b82f6'
  const themeSettings = getThemeSettings(product)
  if (themeSettings) {
    color = themeSettings?.secondaryStyle?.color
  }
  const sections = getSections(product)
  if (sections?.lessonProgress?.progressColor) {
    color = sections.lessonProgress.progressColor
  }
  return color
}

export const getThemeClasses = (value: any, routeName = '') => {
  if (!value) {
    return ''
  }
  const classes = value[routeName ? routeName : useRoute().name]
  return classes || ''
}

export const getMobileTheme = () => {
  if (store.getters.newMobileScreens) {
    const primary = store.getters.mobileTheme.primary
    const secondary = store.getters.mobileTheme.secondary
    return {
      primary: primary ? 'bg-' + primary : '',
      primaryHexCode: store.getters.mobileTheme.primaryHexCode,
      primaryRaw: primary ? primary : '',
      secondaryRaw: secondary ? secondary : '',
      secondary: secondary ? 'bg-' + secondary : '',
      primaryText: primary ? 'text-' + primary : '',
      secondaryText: secondary ? 'text-' + secondary : '',
      secondaryHexCode: store.getters.mobileTheme.secondaryHexCode,
    }
  }
  return {
    primary: '',
    secondary: '',
    primaryText: '',
    secondaryText: '',
  }
}

export function getHeroSpacingClass(spacingConfig: string) {
  let spacing = ''

  switch (spacingConfig) {
    case HeroSpacing.xs:
      spacing = 'py-8'
      break
    case HeroSpacing.small:
      spacing = 'py-12'
      break
    case HeroSpacing.medium:
      spacing = 'py-16'
      break
    case HeroSpacing.large:
      spacing = 'py-24'
      break
    default:
      spacing = 'py-16'
  }

  return spacing
}

export function extractInstructorInfoFromCustomization(
  customization: ProductCustomization
): Instructor {
  const instructor = {} as Instructor

  instructor.heading = customization.instructorHeading
  instructor.name = customization.instructorName
  instructor.title = customization.instructorTitle
  instructor.headshot = customization.instructorHeadshot
  instructor.bio = customization.instructorBio

  return instructor
}

export async function fetchAndSaveBrandData(app) {
  try {
    const hostName = app.$route.query.domain || window.location.hostname
    const savedCookieData = fetchCat()
    const savedLocationId = savedCookieData ? (savedCookieData.location_id || savedCookieData.locationId) : ''
    const { location_id: queryLocationId } = app.$route.query

    let locationId

    if (savedLocationId) {
      locationId = savedLocationId
    }

    if (queryLocationId) {
      // more preference to query location id than saved one
      locationId = queryLocationId
    }

    // if (!locationId) {
    //   throw new Error("Couldn't find the location id")
    // }

    if (!hostName || hostName === 'localhost') {
      throw new Error(`Invalid host name ${hostName}`)
    }

    const domainData: any = []
    const brandData = fetchBrandData(app, hostName, locationId)
    domainData.push(brandData)
    const inPortal = window.location.pathname?.startsWith('/courses')
    if (!locationId && inPortal) {
      const portalSettings = fetchPortalSettings(hostName)
      domainData.push(portalSettings)
    }
    const [brandingData, portalSettingsData] = await Promise.all(domainData)
    if (!brandingData && !portalSettingsData) {
      router.push({ path: '/404' })
    }
    app.$store.commit('SET_CLIENT_PORTAL_DOMAIN_DATA', portalSettingsData)
    return brandingData || {}
  } catch (error) {
    console.error(error)
    return null
  }
}

async function fetchPortalSettings(hostName) {
  try {
    return await restAgent.ClientPortal.getPortalSettings(hostName)
  } catch (error) {
    const { data } = error.response
    if (error.response.status === 404 && data.msg === 'Domain Not Found') {
      console.log('Domain Not Found')
      return null
    }
    return null
  }
}

async function fetchBrandData(app, hostName, locationId) {
  try {
    const { data } = await LoginService.brand({
      domain_name: hostName,
      location_id: locationId,
    })
  
    const { siteInfo, builderSettings } = data
  
    if (siteInfo) {
      app.$store.commit('SAVE_BRAND_DATA', siteInfo)
    }
  
    if (builderSettings.appSettings) {
      const { appSettings } = builderSettings
      const primaryColor = appSettings.backgroundColor
      const secondaryColor = appSettings.theme_color
      if (primaryColor) {
        const color = appSettings.color.toLocaleLowerCase().replace(' ', '-')
        app.$store.commit('UPDATE_MOBILE_THEME', {
          primary: 'mobile-' + color + '-primary',
          primaryHexCode: primaryColor,
          secondary: 'mobile-' + color + '-secondary',
          secondaryHexCode: secondaryColor
        })
      }
    } else {
      app.$store.commit('UPDATE_MOBILE_THEME', {
        primary: 'mobile-default-primary',
        primaryHexCode: '#000000',
        secondary: 'mobile-default-secondary',
        secondaryHexCode: '#0072ef'
      })
    }
  
    return data
  } catch (error) {
    const { data } = error.response
    if (error.response.status === 404 && data.msg === 'Domain Not Found') {
      console.log('Domain Not Found')
      return null
    }
    return null
  }
}

export function getQueryString(search, key) {
  const query = search.slice(search.indexOf('?') + 1, search.length)
  const queryArray = query.split('&')
  for (let i = 0; i < queryArray.length; i++) {
    const pair = queryArray[i].split('=')
    if (pair[0] === key) {
      return decodeURIComponent(pair[1])
    }
  }
  return null
}

export function getHighlightedDescription(description, searchArr, i) {
  try {
    if (i == searchArr.length)
      return description.length > 65
        ? `${description.substring(0, 65)}...`
        : description
    const index = description.toLocaleLowerCase().indexOf(searchArr[0])
    if (index === -1) {
      getHighlightedDescription(description, searchArr, i + 1)
    }
    let startIndex = index - 30 > 0 ? index - 30 : 0
    const endIndex =
      index + 30 > description.length ? description.length : index + 30
    if (index + 30 > description.length) {
      startIndex = startIndex - 50
    }
    return (
      description.substring(startIndex, index) +
      description.substring(index, endIndex)
    )
  } catch (e) {
    console.log(e)
    return description
  }
}

function findSubstringInstances(inputString: string, substring: any) {
  return inputString.split(substring)
}

export function showCollapsableDescription(activePost) {
  const descriptionBeforeAttachingReadmore = activePost.description
  const descriptionAfterAttachingReadmore = activePost.description.substring(
    0,
    MAXIMUM_CHARACTER_LENGTH_BEFORE_READMORE
  ) // if we are having a Read more
  const restOfTheDescription = activePost.description.substring(
    MAXIMUM_CHARACTER_LENGTH_BEFORE_READMORE
  ) // remaining part after descriptionAfterAttachingReadmore

  const listOfAllHtmlTagsInDescriptionBeforeAttachingReadmore =
    findSubstringInstances(descriptionAfterAttachingReadmore, /(<([^>]+)>)/gi)
  const alltagsWithParams =
    listOfAllHtmlTagsInDescriptionBeforeAttachingReadmore.filter((elem) =>
      elem.startsWith('<')
    )

  const numberOfOpeningTags = alltagsWithParams.filter(
    (elem) => elem.split('')[1] !== '/'
  ).length
  const numberOfClosingTags = alltagsWithParams.filter(
    (elem) => elem.split('')[1] === '/'
  ).length

  if (numberOfOpeningTags === numberOfClosingTags) {
    return descriptionAfterAttachingReadmore
  } else {
    const incompleteHtmlTags = []
    alltagsWithParams.forEach((element) => {
      const isAClosingTag = element.split('')[1] === '/'
      if (isAClosingTag === true) {
        const closingTagWithoutSlash = element.replace('/', '')
        if (
          incompleteHtmlTags[incompleteHtmlTags.length - 1] &&
          incompleteHtmlTags[incompleteHtmlTags.length - 1] ===
            closingTagWithoutSlash
        ) {
          incompleteHtmlTags.pop()
        }
      } else {
        const splitedCurrentTagTextArray = element.split(' ')
        const currentTagWithoutParams =
          splitedCurrentTagTextArray.length === 1
            ? splitedCurrentTagTextArray[0]
            : splitedCurrentTagTextArray[0] + '>'
        incompleteHtmlTags.push(currentTagWithoutParams)
      }
    })

    if (incompleteHtmlTags[0]) {
      const closingTagOfFirstIncompleteTag = incompleteHtmlTags[0].replace(
        '<',
        '</'
      )
      const indexOfTheFirstClosingAnchorTag = restOfTheDescription.indexOf(
        closingTagOfFirstIncompleteTag
      )
      const closingTagLength =
        closingTagOfFirstIncompleteTag !== undefined
          ? closingTagOfFirstIncompleteTag.length
          : 0
      const actualIndexOfClosingAnchorTag =
        MAXIMUM_CHARACTER_LENGTH_BEFORE_READMORE +
        indexOfTheFirstClosingAnchorTag +
        closingTagLength
      return descriptionBeforeAttachingReadmore.substring(
        0,
        actualIndexOfClosingAnchorTag
      )
    }
  }
  return descriptionAfterAttachingReadmore
}

export function checkNodeIsCompleted(
  allCompletedPosts: Set<string>,
  entity: CategoryTreeNode
) {
  if (entity?.type === CategoryTreeNodeType.Post) {
    return allCompletedPosts.has(entity.id)
  } else {
    const category = entity.node as Category

    if (!category.posts) return false

    const completedPosts = category.posts.filter((p: Post) =>
      allCompletedPosts.has(p.id)
    )

    return completedPosts.length === category.posts.length
  }
}

// export function setFavicon(url: string) {
//   try {
//     if (url) {
//       const link: any =
//         document.querySelector("link[rel*='icon']") ||
//         document.createElement('link')
//       link.type = 'image/x-icon'
//       link.rel = 'shortcut icon'
//       link.href = url
//       document.getElementsByTagName('head')[0].appendChild(link)
//     }
//   } catch (error) {
//     console.error('error while setting favicon --> ', error)
//   }
// }

// export function setPageTitle(title: string) {
//   if (title) {
//     document.title = title
//   }
// }

export function sliceLongText(text: string) {
  return text && text.length > SLICED_TITLE_LENGTH
    ? `${text.slice(0, SLICED_TITLE_LENGTH)}...`
    : text
}

export const getFileType = (filename: string): string => {
  const parts = filename.split('.')
  const extension = parts[parts.length - 1]
  return extension
}

export const acceptableFile = (file: any): boolean => {
  if (!file) return false

  const fileType = file.type

  const fileName = file.name
  const typeFromName = getFileType(fileName)
  if (
    nonAcceptableFileTypes.has(fileType) ||
    nonAcceptableFileTypes.has(typeFromName)
  ) {
    return false
  }

  return true
}
export function getOptionAlphabet(sequenceNumber: number) {
  const baseValue = 96
  return String.fromCharCode(baseValue + sequenceNumber).toUpperCase()
}

export function isAppInstalled() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  if (document.referrer.startsWith('android-app://')) {
    return true
  } else if ((window.navigator as any).standalone || isStandalone) {
    return true
  }
  return false
}

export function notify({
  type,
  title,
  description,
  time,
}: {
  type: string
  title: string
  description?: string
  time?: string
}) {
  notification[type]({
    content: title,
    meta: description,
    duration: time ? time : 5000,
    keepAliveOnHover: true,
  })
}

export function redirectTo(path: string, query?: string, newTab = false) {
  const newURL = `${window.location.origin}/${path}?${query ? query : ''}`
  if (newTab) {
    window.open(newURL, "_blank");
  } else {
    window.location.href = newURL;
  }
}

export const formatNameForAvatar = (name: string) => {
  const nameArr = name?.split(' ')
  return nameArr
    ?.map(item => item[0])
    .join('')
    .toUpperCase()
}

export const capitalizeFirstLetterName = (name: string) => {
  return name
    .split(' ')
    .map(item => `${item[0]?.toUpperCase()}${item?.slice(1)}`)
    .join(' ')
}

export function isClientPortal() {
  const clientPortalPrefixPath = '/courses'
  return window.location.pathname.startsWith(clientPortalPrefixPath)
}

export function redirect(
  to: RouteLocationNormalized, 
  from: RouteLocationNormalized, 
  next: NavigationGuardNext
) {
  const routeVersionSuffix = '-v2'
  const isLoginRoute = to.name === 'login'
  const clientPortalPrefixPath = '/courses'
  if (isClientPortal() && !to.fullPath.startsWith(clientPortalPrefixPath)) {
    const updatedParams: RouteLocationNormalized = {
      ...to,
      fullPath: !isLoginRoute ? clientPortalPrefixPath + to.fullPath : to.fullPath,
      path: !isLoginRoute ? clientPortalPrefixPath + to.path : to.path,
      name: String(to.name) + routeVersionSuffix
    }
    return next(updatedParams)
  }

  return next();
}